import React, {useState, useEffect, useCallback} from "react";
import Grid from "../../components/Grid";
import {useAuthWithRoute, useUrlState} from "../../utils/hooks";
import {useVsiAppContext} from "../../stores/VsiApp";
import {VsiH4, Fade, VsiBubble} from "../../styled/App.styled";
import {PutAuth} from "../../utils/Verbs";
import VsiAlert from "../../components/VsiAlert";

import {Card, Nav, NavItem, NavLink} from "reactstrap";
import {
    operatingCostDefs,
} from "../../utils/gridColumnDefs/user/operatingCostDefs";
import {VsiSelectLabel} from "../../styled/VsiLabeledSelect.styled";

const allTagContexts = [
    {value: "cost", label: "Operating Cost"},
    // other tabs...
];

const alertTime = 3000;

export default function OperatingCost() {
    const {theme, client, getAccessTokenSilently} = useVsiAppContext();
    const [opData, setOpData] = useState([]);
    const [alerts, setAlerts] = useState([]);

    const [fadeAwayMessage, setMessage] = useState(null);

    const [showMessage, setShowMessage] = useState(false);
    const [fadeType, setFadeType] = useState("success");
    const [columnDefs, setColumnDefs] = useState([]);

    const [params, setParams] = useUrlState({
        context: "cost"
    });

    const {context} = params;

    let route = "";
    if (context === "cost") {
        route = "operating_costs";
    } else {
        // Other tabs ...
    }

    const [data, {isLoading}] = useAuthWithRoute(`/api/v6/admin/${route}/client/${client.clientId}`, route);

    const putSingleOp = async data => {
        try {
            const token = await getAccessTokenSilently();
            const response = await PutAuth(`/api/v6/admin/${route}/${data._id}`, data, token);
            if (response && response.status === 201) {
                showFadeAwayMessage("Record saved successfully.", "success");
                setOpData(prev =>
                    prev.map(datum => {
                        if (datum._id === data._id) {
                            return {
                                ...datum,
                                ...data
                            };
                        }
                        return datum;
                    }));
            } else {
                showFadeAwayMessage("Something went wrong. Please try again.", "warning");
            }
            return response;
        } catch (error) {
            console.error(error);
            showFadeAwayMessage("Something went wrong...", "warning");
            return error;
        }
    };

    const showFadeAwayMessage = (messageText, type = "success") => {
        setMessage(messageText);
        setFadeType(type);
        setShowMessage(true);
        setTimeout(() => {
            setShowMessage(false);
        }, 2500);
    };

    const onRowEditingStopped = useCallback(async (event) => {
        // update a row of OpCost to Mongo after a cell is edited
        const updatedData = {...event.data};
        Object.keys(event.data).forEach(key => {
            if (updatedData[key] === "undefined" || (updatedData[key] == null && updatedData[key] !== 0)) {
                delete updatedData[key];
            }
        });
        if (context === "cost") {
            putSingleOp(updatedData);
        }
    }, [context, opData]);

    const toggleAlert = id => {
        const alertUpdate = [...alerts];
        const alertIndex = alerts.findIndex(alert => alert.alertId === id);
        alertUpdate[alertIndex].isOpen = false;
        setAlerts(alertUpdate);
    };

    const onContextChange = ({value}) => {
        setParams({
            context: value
        });
    };

    useEffect(() => {
        setOpData(data || []);
    }, [data, context]);

    useEffect(() => {
        if (alerts.length > 0) {
            const interval = setInterval(() => {
                let foundAlerts = false;
                const newAlerts = [...alerts].filter(alert => alert.isOpen).map(alert => {
                    const isExpired = (Date.now() - alert.created) > alertTime;
                    if (alert.isOpen !== !isExpired) foundAlerts = true;
                    return {
                        ...alert,
                        isOpen: !isExpired
                    };
                });

                if (foundAlerts) setAlerts(newAlerts);

            }, 1000);

            return () => clearInterval(interval);
        }
    }, [alerts]);

    useEffect(() => {
        if (context === "cost") {
            setColumnDefs(operatingCostDefs);
        } else {
            // ...
            setColumnDefs([]);
        }
    }, [context]);

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            {alerts.map((alert) => (
                <div className={"w-100 d-flex justify-content-center"}>
                    <VsiAlert color={alert.error ? "danger" : "success"} isOpen={alert.isOpen}
                              toggle={() => toggleAlert(alert.alertId)}>
                        <i className={`fa-solid fa-${alert.error ? "exclamation" : "check"} pe-2`}/>
                        {alert.message}
                    </VsiAlert>
                </div>
            ))}
            {showMessage &&
                    <Fade className={`fade-away-message ${fadeType === "success" ? "success" : "warning"}`}>
                        {fadeAwayMessage}
                    </Fade>}
            <VsiH4>Operating Cost</VsiH4>
            <VsiBubble>
                <VsiSelectLabel>
                    Context
                </VsiSelectLabel>
                <Nav
                    tabs
                >
                    {allTagContexts.map((tag) => (
                        <NavItem
                            key={`button-${tag.value}-context`} style={{cursor: 'pointer'}}>
                            <NavLink
                                active={context === tag.value}
                                disabled={context === tag.value}
                                onClick={() => onContextChange({value: tag.value})}
                            >
                                {tag.label}
                            </NavLink>
                        </NavItem>
                    ))}
                </Nav>
                <Grid data={opData}
                      isLoading={isLoading}
                      exportCsv={true}
                      onRowEditingStopped={onRowEditingStopped}
                      columnDefs={columnDefs}
                      gridText={"Double click a row to edit then press enter to commit changes."}/>
            </VsiBubble>
        </Card>
    );
}
